import React from "react";
import { ChangeEvent } from "react";
import { Link, Prompt, RouteComponentProps } from "react-router-dom";

import ViewToolbar from "../ViewToolbar";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";

import { apiGet, apiPatch, apiPost, apiDelete } from "../../Api";
import withTitle from "../withTitle";
import { formatName } from "../../Utilities";

import "../../blocks.css";
// import "../../icomoon.css";
// import "../../indicator.css";
// import "../../list.css";
import "../../spinner.css";
// import "../../table.css";
import "../../user.css";
import "../viewer.css";
import DeletionConfirmationDialog from "../DeletionConfirmationDialog";

interface IEmployeeDetailsState {
    error?: string;
    isLoading: boolean;
    group?: any;

    name: string;
    description: string;
    isWellKnown: boolean;

    hasChanged: boolean;
    isSubmitting: boolean;
    submitError?: string;

    isDeleteDialogOpen: boolean;
}


class GroupDetailsView extends React.Component<RouteComponentProps<any>, IEmployeeDetailsState> {
    constructor(props: Readonly<RouteComponentProps<any>>) {
        super(props);

        this.state = {
            isLoading: true,

            name: "",
            description: "",
            isWellKnown: false,

            hasChanged: false,
            isSubmitting: false,

            isDeleteDialogOpen: false,
        };

        this.submitChanges = this.submitChanges.bind(this);
        this.cancelChanges = this.cancelChanges.bind(this);
        this.handleInputChanged = this.handleInputChanged.bind(this);
        this.confirmDelete = this.confirmDelete.bind(this);
        this.closeDeleteDialog = this.closeDeleteDialog.bind(this);
        this.deleteGroup = this.deleteGroup.bind(this);
    }

    public async componentDidMount() {
        const groupId = (this.props.match && this.props.match.params.id);

        try {
            if (groupId) {
                // Load existing group
                const group = await apiGet<any>(`employees/groups/${groupId}`);

                this.setState({
                    group,
                    isLoading: false,

                    name: group.name,
                    description: group.description,
                    isWellKnown: group.isWellKnown,
                });
            }
            else {
                this.setState({
                    isLoading: false,
                });
            }
        }
        catch (e) {
            this.setState({
                error: e.message,
                isLoading: false
            });
        }
    }

    public render() {
        if (this.state.isLoading) {
            return (
                <div className="viewer-root">
                    <div className="spinner-center lds-dual-ring-big" />
                </div>
            );
        }

        if (this.state.error) {
            return (
                <div className="viewer-root">
                    <span className="error-text">{this.state.error}</span>
                </div>
            );
        }

        return (
            <div className="viewer-root with-actionbar">
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <ViewToolbar>
                    <OverflowMenuItem>
                        <ToolbarMenuItem right="employee:delete" condition={!!this.state.group && !this.state.isWellKnown}>
                            <button onClick={this.confirmDelete}><i className="icon-trash" /><span>Delete Group</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <DeletionConfirmationDialog isOpen={this.state.isDeleteDialogOpen} onConfirm={this.deleteGroup} onRequestClose={this.closeDeleteDialog} onSuccess={this.cancelChanges} description={null}>
                    <span>
                        Are you sure you want to delete <b>{this.state.name}</b>?<br />
                        The group has {this.state.group ? this.state.group.members.length : 0} member(s)
                    </span>
                </DeletionConfirmationDialog>
                <div className="column-root input-form last-row">
                    <section className="info-block">
                        <header>
                            <h3>Group Details</h3>
                        </header>

                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-name">Name</label>
                                <input type="text" className="input-light" id="frm-name" name="name" value={this.state.name} onChange={this.handleInputChanged} disabled={this.state.isWellKnown} autoFocus={!this.state.group} />
                            </div>
                        </section>
                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-description">Description</label>
                                <input type="text" className="input-light" id="frm-description" name="description" value={this.state.description} onChange={this.handleInputChanged} disabled={this.state.isWellKnown} />
                            </div>
                        </section>
                        {this.state.isWellKnown
                            ? <section>
                                <div className="input-form-group">
                                    <small className="text-light">This group is a built-in well-known group.  It is used by the system internally and cannot be edited.</small>
                                </div>
                            </section>
                            : null}
                    </section>
                    {this.state.group
                        ? <table className="section-table">
                            <thead>
                                <tr>
                                    <th colSpan={2}>Members</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.group.members && this.state.group.members.length > 0
                                    ? this.state.group.members.map((a: any) => <tr>
                                        <td>{formatName(a.name)}</td>
                                        <td className="col-smallest"><Link to={`/employees/${a.id}`}>View</Link></td>
                                    </tr>)
                                    : <tr><td colSpan={2}>No members</td></tr>}
                            </tbody>
                        </table>
                        : <section className="block-spacer" />
                    }
                </div>
                &nbsp;
                {this.state.isSubmitting
                    ? <div className="spinner lds-dual-ring" />
                    : this.state.submitError
                        ? <span className="error-text">{this.state.submitError}</span>
                        : null}
                {this.state.isWellKnown
                    ? <div className="action-panel">
                        <button className="button blue push-right" onClick={this.cancelChanges}>Ok</button>
                    </div>
                    : <div className="action-panel">
                        <button className="button blue btn-confirm push-right" disabled={this.state.isSubmitting} onClick={this.submitChanges}>Save</button>
                        <button className="button blue btn-cancel" disabled={this.state.isSubmitting} onClick={this.cancelChanges}>Cancel</button>
                    </div>
                }
            </div>
        );
    }

    private async submitChanges() {
        try {
            if (this.state.group) {
                // Update existing group
                this.setState({
                    isSubmitting: true,
                    submitError: undefined
                });

                const update = {
                    name: this.state.name,
                    description: this.state.description,
                };

                await apiPatch(`employees/groups/${this.state.group.id}`, update);

                this.setState({
                    hasChanged: false,
                    isSubmitting: false
                });
            }
            else {
                // Create new employee
                const creationInfo = {
                    name: this.state.name,
                    description: this.state.description,
                };

                this.setState({
                    isSubmitting: true,
                    submitError: undefined
                });

                await apiPost("employees/groups", creationInfo);

                this.setState({
                    hasChanged: false,
                    isSubmitting: false
                });
            }
            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                isSubmitting: false,
                submitError: e.message || "Could not save group"
            });
        }
    }

    private cancelChanges() {
        this.props.history.goBack();
    }

    private handleInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            hasChanged: true,
            [name]: value
        } as any)
    }

    private confirmDelete() {
        this.setState({
            isDeleteDialogOpen: true
        });
    }

    private async deleteGroup() {
        this.setState({
            hasChanged: false,
        });

        await apiDelete(`employees/groups/${this.state.group.id}`);
    }

    private closeDeleteDialog() {
        this.setState({
            isDeleteDialogOpen: false
        })
    }
}

export default withTitle("Group Details", GroupDetailsView);
